import {
  Component,
  OnInit,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';

@Component({
  selector: 'app-dashboard-approval-internal-documents',
  templateUrl: './dashboard-approval-internal-documents.component.html',
  styleUrls: ['./dashboard-approval-internal-documents.component.scss'],
})
export class DashboardApprovalInternalDocumentsComponent implements OnInit {
  documents: any[] = [];
  documentsLoading: boolean = false;
  tableLoading: boolean = false; // Loading específico para a tabela
  showAttachmentModal: boolean = false;
  newAttachments: FilePicked[] = [];
  loading: boolean = false;
  uploadLoading: boolean = false;
  clearAttachments: boolean = false;
  documentName: string = '';
  departmentForm: FormGroup;
  documentNameForm: FormGroup;
  departmentOptions: { label: string; value: number }[] = [];
  loadingDepartments: boolean = false;
  filterDepartmentForm: FormGroup;
  filterDepartmentOptions: { label: string; value: number | null }[] = [];
  selectedDepartmentFilter: number | null = null;
  showDepartmentDropdown: boolean = false;

  deletingDocumentId: number | null = null;
  showDeleteModal: boolean = false;
  documentToDelete: any = null;
  deletingLoading: boolean = false; // Spinner loading durante exclusão

  // Modal de visualização
  showDocumentModal: boolean = false;
  selectedDocument: any = null;

  @ViewChild('departmentDropdown') departmentDropdown: ElementRef;

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    if (
      this.departmentDropdown &&
      !this.departmentDropdown.nativeElement.contains(event.target)
    ) {
      this.closeDepartmentDropdown();
    }
  }

  constructor(
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private fb: FormBuilder
  ) {
    // FormGroup para nome do documento (obrigatório)
    this.documentNameForm = this.fb.group({
      documentName: ['', [Validators.required]]
    });
    
    // FormGroup para departamento (será ajustado no ngOnInit se for master)
    this.departmentForm = this.fb.group({
      department: [null]
    });
    
    // FormGroup para filtro de departamento
    this.filterDepartmentForm = this.fb.group({
      department: [null]
    });
  }

  ngOnInit(): void {
    this.documentsLoading = true;
    // Inicializar com "Todos" (null)
    this.selectedDepartmentFilter = null;
    this.filterDepartmentForm.patchValue({ department: null });
    
    // Se for master, carregar departamentos para o filtro e modal
    if (this.isMasterUser()) {
      this.loadFilterDepartments(); // Carregar departamentos para o filtro
      this.departmentForm.get('department')?.setValidators([Validators.required]);
      this.departmentForm.get('department')?.updateValueAndValidity();
      this.getDepartments();
    }
    
    // Buscar documentos
    this.handleGetDocuments();
    
    // Removido o listener de valueChanges pois agora usamos selectDepartmentFilter diretamente
  }

  async handleGetDocuments(showTableLoading: boolean = false) {
    // Se for chamado pelo botão atualizar, mostrar loading na tabela
    if (showTableLoading) {
      this.tableLoading = true;
    }
    
    const internalDocuments = await this.getCompleteFileUploadDocuments();
    
    let filteredDocuments = internalDocuments.map((item) => ({
      ...item,
      slug: 'internal-documents',
    }));

    // Se não houver filtro selecionado e for usuário comum, aplicar filtro no front-end
    // (segurança adicional caso a API não filtre automaticamente)
    // Se houver filtro selecionado, a API já filtrou
    const user = this.authService.user || this.authService.getUser();
    const isMaster = this.isMasterUser();
    const isCommonUser = user?.type_permission === 'common';

    if (!this.selectedDepartmentFilter && !isMaster && isCommonUser) {
      // Para usuário comum sem filtro selecionado, pegar o departamento de user.department.id (prioridade) ou active_register
      const userDepartmentId = user?.department?.id || 
                                user?.active_register?.register?.department?.id;
      const userDepartmentName = (user?.department?.name || 
                                  user?.active_register?.register?.department?.name)?.toLowerCase().trim();

      if (userDepartmentId || userDepartmentName) {
        filteredDocuments = filteredDocuments.filter((doc) => {
          // Tentar comparar por ID primeiro (mais confiável)
          if (userDepartmentId && doc.department_id) {
            return doc.department_id === userDepartmentId;
          }
          
          // Se não tiver ID, comparar por nome (lowercase)
          if (userDepartmentName && doc.department_name) {
            return doc.department_name.toLowerCase().trim() === userDepartmentName;
          }

          // Se o documento não tiver departamento definido e o usuário comum tem departamento, não mostrar
          // (documentos sem departamento não devem aparecer para usuários comuns)
          if (userDepartmentId || userDepartmentName) {
            return false;
          }

          return true;
        });
      } else {
        // Se usuário comum não tem departamento definido, não mostrar nenhum documento
        filteredDocuments = [];
      }
    }

    this.documents = filteredDocuments;
    this.documentsLoading = false;
    this.tableLoading = false; // Desativar loading da tabela
    this.cdr.detectChanges();
  }

  async getCompleteFileUploadDocuments(): Promise<any[]> {
    const registerUuid = this.route.snapshot.params.id;

    try {
      const params: any = {
        register_id: registerUuid,
      };
      
      // Adicionar filtro de departamento se houver seleção (e não for "Todos")
      if (this.selectedDepartmentFilter !== null) {
        params.department = this.selectedDepartmentFilter;
      }

      const completeFileRes = await this.api.get({
        route: 'api/registration/complete_file_upload',
        params: params,
        token: true,
      });

      let allDocuments: any[] = [];

      if (completeFileRes && completeFileRes.get_info) {
        if (Array.isArray(completeFileRes.get_info)) {
          allDocuments = completeFileRes.get_info;
        } else {
          allDocuments = [];
        }
      } else if (Array.isArray(completeFileRes)) {
        allDocuments = completeFileRes;
      } else if (completeFileRes && completeFileRes.data !== undefined) {
        if (Array.isArray(completeFileRes.data)) {
          allDocuments = completeFileRes.data;
        } else if (completeFileRes.data === '-' || !completeFileRes.data) {
          allDocuments = [];
        } else {
          allDocuments = [completeFileRes.data];
        }
      }

      allDocuments = allDocuments
        .filter((item) => {
          if (!item || item === null || item === undefined) {
            return false;
          }
          return item.file !== undefined && item.file !== null;
        })
        .map((item) => {
          return {
            ...item,
            created_at:
              item.date_created ||
              item.created_at ||
              item.created ||
              item.timestamp ||
              null,
            file: item.file || null,
            name: item.name || item.label || item.description || null,
            description: item.description || null,
            user_name: item.user_name || null,
            user_id: item.user_id || item.user || null,
            department_name: item.department_name || null,
            department_id: item.department_id || item.department?.id || null,
          };
        });

      return allDocuments;
    } catch (error) {
      console.error(
        'Erro ao buscar documentos do complete_file_upload:',
        error
      );
      return [];
    }
  }

  toggleAttachmentModal() {
    // Limpar campos antes de fechar ou ao abrir
    this.clearFields();
    
    this.clearAttachments = !this.showAttachmentModal;
    this.showAttachmentModal = !this.showAttachmentModal;
    
    if (this.showAttachmentModal && this.isMasterUser() && this.departmentOptions.length === 0) {
      this.getDepartments();
    }
  }

  clearFields() {
    this.newAttachments = [];
    this.documentName = '';
    
    // Limpar e resetar o form de nome do documento
    this.documentNameForm.patchValue({ documentName: '' });
    this.documentNameForm.get('documentName')?.markAsUntouched();
    this.documentNameForm.get('documentName')?.markAsPristine();
    this.documentNameForm.get('documentName')?.setErrors(null);
    this.documentNameForm.get('documentName')?.updateValueAndValidity({ onlySelf: true });
    
    // Limpar e resetar o form de departamento
    this.departmentForm.patchValue({ department: null });
    this.departmentForm.get('department')?.markAsUntouched();
    this.departmentForm.get('department')?.markAsPristine();
    this.departmentForm.get('department')?.setErrors(null);
    this.departmentForm.get('department')?.updateValueAndValidity({ onlySelf: true });
  }

  setFiles(files: FilePicked[]) {
    this.newAttachments = [...files];
    this.cdr.detectChanges();
  }

  onDocumentNameChange(value: string) {
    this.documentName = value || '';
    // Atualizar o FormGroup também
    this.documentNameForm.patchValue({ documentName: value || '' }, { emitEvent: false });
    // Forçar detecção de mudanças para atualizar o estado do botão
    this.cdr.detectChanges();
  }


  async uploadAttachment() {
    if (this.newAttachments.length === 0) {
      this.toast.show('info', 'Atenção', 'Selecione um arquivo para anexar');
      return;
    }

    // Validar o FormGroup antes de continuar
    if (this.documentNameForm.invalid) {
      this.documentNameForm.markAllAsTouched();
      this.toast.show('info', 'Atenção', 'Informe o nome do documento');
      return;
    }

    const documentNameValue = this.documentNameForm.get('documentName')?.value || '';

    if (!documentNameValue || documentNameValue.trim() === '') {
      this.toast.show('info', 'Atenção', 'Informe o nome do documento');
      return;
    }

    this.uploadLoading = true;

    try {
      const file = this.newAttachments[0].file;
      const registerUuid = this.route.snapshot.params.id;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', '51');

      if (registerUuid) {
        formData.append('register', registerUuid);
      }

      if (documentNameValue && documentNameValue.trim()) {
        formData.append('description', documentNameValue.trim());
      }

      // Adicionar department_id ao formData
      let departmentId: number | null = null;

      if (this.isMasterUser()) {
        // Para master, pegar do select (obrigatório)
        const selectedDepartmentId = this.departmentForm.get('department')?.value;
        if (selectedDepartmentId) {
          departmentId = selectedDepartmentId;
        }
      } else {
        // Para usuário comum, pegar automaticamente do user.department.id (prioridade) ou active_register
        const user = this.authService.user || this.authService.getUser();
        if (user) {
          departmentId = user.department?.id || 
                         user.active_register?.register?.department?.id || 
                         null;
        }
      }

      if (departmentId) {
        formData.append('department_id', departmentId.toString());
      }

      await this.api.post({
        route: 'api/registration/complete_file_upload',
        body: formData,
        token: true,
        noDefautHeaders: true,
      });

      this.toast.show('info', 'Sucesso', 'Documento anexado com sucesso!');

      // Limpar campos e fechar modal
      this.clearFields();
      this.toggleAttachmentModal();

      // Ativar apenas o skeleton da tabela (não o skeleton do conteúdo todo)
      this.tableLoading = true;
      this.cdr.detectChanges(); // Forçar atualização da view para mostrar o skeleton
      await this.handleGetDocuments(true); // Passar true para manter o skeleton durante o carregamento
    } catch (error: any) {
      console.error('Erro ao anexar documento:', error);
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao anexar documento'
      );
    } finally {
      this.uploadLoading = false;
    }
  }

  formateDate(date: string) {
    if (!date) return '';
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return { day, hours };
  }

  getAttachmentDisabled(): boolean {
    const hasFile = this.newAttachments.length > 0;
    const isDocumentNameValid = this.documentNameForm.get('documentName')?.valid;
    
    // Se for master, o departamento é obrigatório (validação do FormGroup)
    // Para usuário comum, o departamento é pego automaticamente
    if (this.isMasterUser()) {
      const hasDepartment = this.departmentForm.get('department')?.value !== null && 
                           this.departmentForm.get('department')?.valid;
      return !hasFile || !isDocumentNameValid || !hasDepartment;
    }
    
    // Para usuário comum, não precisa validar departamento (é automático)
    return !hasFile || !isDocumentNameValid;
  }

  openDocument(item: any) {
    if (!item || !item.file) {
      this.toast.show('error', 'Erro', 'Arquivo não disponível');
      return;
    }
    
    // Fechar modal anterior se estiver aberto
    if (this.showDocumentModal) {
      this.showDocumentModal = false;
    }
    
    // Preparar o documento para o modal
    // O modal espera um objeto com propriedade 'file' contendo o caminho
    this.selectedDocument = {
      file: item.file
    };
    
    // Forçar detecção de mudanças antes de abrir o modal
    this.cdr.detectChanges();
    
    // Abrir o modal após um pequeno delay para garantir que o Angular processe a mudança do arquivo
    setTimeout(() => {
      this.showDocumentModal = true;
      this.cdr.detectChanges();
    }, 10);
  }

  closeDocumentModal() {
    this.showDocumentModal = false;
    this.selectedDocument = null;
  }

  deleteDocument(item: any) {
    if (!item.id) {
      this.toast.show('error', 'Erro', 'ID do documento não encontrado');
      return;
    }

    if (this.deletingDocumentId === item.id) {
      return;
    }

    this.documentToDelete = item;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.documentToDelete = null;
  }

  async confirmDeleteDocument() {
    if (!this.documentToDelete || !this.documentToDelete.id) {
      return;
    }

    // Mostrar spinner loading (modal continua aberto)
    this.deletingLoading = true;
    this.deletingDocumentId = this.documentToDelete.id;

    try {
      const registerUuid = this.route.snapshot.params.id;

      const url = this.api.getUrl({
        route: 'api/registration/complete_file_upload',
        params: {
          register_id: registerUuid,
          file_id: this.documentToDelete.id,
        },
      });

      const headers = this.api.getHeaders(true, false);
      const httpHeaders = new HttpHeaders(headers);

      await this.http
        .delete(url, {
          headers: httpHeaders,
        })
        .toPromise();

      this.toast.show('info', 'Sucesso', 'Documento excluído com sucesso!');

      // Desativar spinner e fechar modal
      this.deletingLoading = false;
      this.closeDeleteModal();

      // Ativar skeleton da tabela e buscar documentos novamente
      this.tableLoading = true;
      await this.handleGetDocuments();
    } catch (error: any) {
      console.error('Erro ao excluir documento:', error);
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao excluir documento'
      );
      // Desativar apenas o spinner, manter modal aberto em caso de erro
      this.deletingLoading = false;
    } finally {
      this.deletingDocumentId = null;
    }
  }

  getDeleteModalTitle() {
    return 'Confirmar exclusão';
  }

  getDeleteModalMessage() {
    return 'Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.';
  }

  getDeleteConfirmText() {
    return 'Sim, excluir';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  hasDocuments(): boolean {
    return this.documents && this.documents.length > 0;
  }

  isMasterUser(): boolean {
    return this.authService.isMasterUser();
  }

  isDocumentSentByCurrentUser(document: any): boolean {
    const user = this.authService.user || this.authService.getUser();
    if (!user || !document) {
      return false;
    }

    // Comparar por ID do usuário (mais confiável)
    if (document.user_id && user.id) {
      return document.user_id === user.id;
    }

    // Comparar por nome (fallback)
    if (document.user_name && user.first_name && user.last_name) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      return document.user_name.toLowerCase().trim() === fullName.toLowerCase().trim();
    }

    return false;
  }

  canDeleteDocument(document: any): boolean {
    // Master pode excluir qualquer documento
    if (this.isMasterUser()) {
      return true;
    }
    
    // Usuário comum só pode excluir documentos que ele enviou
    return this.isDocumentSentByCurrentUser(document);
  }

  getSenderName(document: any): string {
    if (this.isDocumentSentByCurrentUser(document)) {
      return 'Você';
    }
    return document.user_name || '--';
  }

  toggleDepartmentDropdown() {
    this.showDepartmentDropdown = !this.showDepartmentDropdown;
  }

  closeDepartmentDropdown() {
    this.showDepartmentDropdown = false;
  }

  selectDepartmentFilter(option: { label: string; value: number | null }) {
    this.filterDepartmentForm.patchValue({ department: option.value });
    this.selectedDepartmentFilter = option.value;
    this.closeDepartmentDropdown();
    // Ativar loading da tabela antes de buscar documentos
    this.tableLoading = true;
    this.cdr.detectChanges();
    this.handleGetDocuments(true);
  }

  getSelectedDepartmentLabel(): string {
    if (this.selectedDepartmentFilter === null) {
      return 'Todos';
    }
    const selected = this.filterDepartmentOptions.find(
      (opt) => opt.value === this.selectedDepartmentFilter
    );
    return selected ? selected.label : 'Todos';
  }

  async getDepartments() {
    if (this.loadingDepartments) return;
    
    this.loadingDepartments = true;
    try {
      const response: any = await this.api.get({
        route: 'department/',
        token: true,
      });

      this.departmentOptions = (response || []).map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
      this.toast.show('error', 'Erro', 'Erro ao carregar departamentos');
    } finally {
      this.loadingDepartments = false;
    }
  }

  async loadFilterDepartments() {
    try {
      const response: any = await this.api.get({
        route: 'department/',
        token: true,
      });

      // Adicionar opção "Todos" no início
      this.filterDepartmentOptions = [
        { label: 'Todos', value: null },
        ...(response || []).map((item: any) => ({
          label: item.name,
          value: item.id,
        }))
      ];
    } catch (error) {
      console.error('Erro ao buscar departamentos para filtro:', error);
    }
  }

  renderIcon(file: any) {
    if (!file) return '';

    // Se file é um objeto com url.file, usar url.file
    // Se file é um objeto com file, usar file
    // Se file é uma string, usar diretamente
    let fileUrl = '';
    if (typeof file === 'object') {
      if (file.url && file.url.file) {
        fileUrl = file.url.file;
      } else if (file.file) {
        fileUrl = file.file;
      } else {
        return '';
      }
    } else if (typeof file === 'string') {
      fileUrl = file;
    } else {
      return '';
    }

    const fileExtension = fileUrl.split('.').pop()?.toLowerCase();

    let extension = '';

    switch (fileExtension) {
      case 'pdf':
        extension = 'icon_filepdf';
        break;
      case 'jpg':
      case 'jpeg':
        extension = 'icon_filejpg';
        break;
      case 'png':
        extension = 'icon_filepng';
        break;
      default:
        extension = 'icon_filejpg';
        break;
    }

    return `/assets/images/${extension}.svg`;
  }
}

